/**
 * InferenceMiniChart - Compact sparkline for inference rows.
 *
 * Mirrors the visual language of ``ModelTrainingsTab.renderPerformanceChart``
 * (one row of the Models table on the pipeline detail page) but adapted
 * for forecasts where we have NO ground truth: only the model's
 * predicted p10 / p50 / p90 quantile bands plus optional historical
 * context preceding the forecast.
 *
 * Layout choices:
 *   * History line (when available): neutral text-primary-at-70%
 *     alpha, slightly thinner. Provides the lead-in trend the
 *     forecast extrapolates from.
 *   * p50 line (predicted median): solid primary palette, thicker.
 *     Anchored at the last historical value so the line is visually
 *     continuous through "now".
 *   * p10 / p90 band: filled area between the two quantile lines,
 *     light primary tint. Tapers from zero width at the cutover
 *     outward as uncertainty grows.
 *   * Cutover marker: faint dashed vertical line separating
 *     observed from predicted (only when history is supplied).
 *   * Headline chip (top-right): the FINAL p50 value, colour-coded
 *     by band width as a confidence proxy.
 *   * No axes, grid, or legend - it's a sparkline.
 *
 * Hover tooltip shows the full per-step quartet
 * (step #, p10, p50, p90, band width) for forecast points, or
 * "Actual: <value>" for history points.
 */
import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ResponsiveLine } from '@nivo/line';
import { accentFor } from '../common/ForecastCharts';

const fmtNum = (v) => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(1)}k`;
  return v.toFixed(abs >= 1 ? 1 : 3);
};

// Single source of truth for the tooltip's container style so the
// history / anchor / forecast tooltip variants stay visually
// consistent with each other.
const tooltipStyle = (theme) => ({
  background: theme.palette.background.paper,
  padding: '6px 10px',
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
  boxShadow: theme.palette.boxShadow,
  minWidth: 130,
});

// Predictions in the inferences collection are stored as
// ``[batch][horizon][channels]`` arrays; the FE list endpoint already
// flattens to ``[horizon]`` for the sparkline budget, but the detail
// endpoint returns the raw shape. This helper accepts either.
const flatten = (arr) => {
  if (!Array.isArray(arr)) return [];
  // Detect [batch][horizon][channels] -> use first batch
  if (Array.isArray(arr[0]) && Array.isArray(arr[0][0])) {
    return arr[0].map((step) => (Array.isArray(step) ? step[0] : step));
  }
  // Detect [horizon][channels] -> single batch already
  if (Array.isArray(arr[0])) {
    return arr.map((step) => (Array.isArray(step) ? step[0] : step));
  }
  return arr;
};

/**
 * Compute the final-step p50 value and a band-width-derived colour
 * for the headline ("Final" column on the inferences list). Exported
 * so the row can render the same number / colour the chart used to
 * stamp on top of the sparkline.
 *
 * Returns ``{ value, bandPct, severity }`` where:
 *   * value     - p50 at the last forecast step (number | null)
 *   * bandPct   - (p90 - p10) / |p50| * 100 at the last step
 *                  (null if any quantile is missing or p50 is 0)
 *   * severity  - 'success' | 'primary' | 'warning' | 'error' |
 *                  'neutral' - confidence proxy bucketed by bandPct
 */
export const predictionFinal = (prediction) => {
  const point = flatten(prediction?.point);
  const q10 = flatten(prediction?.quantiles?.['0.1']);
  // NOTE: `flatten()` ALWAYS returns an array (never null), and `[]`
  // is truthy in JS, so a naive `flatten(...) || point` never
  // falls back. Foundation models populate `quantiles`; custom
  // trained models per the API spec do NOT (point-only). Use an
  // explicit length check so custom inferences still render.
  const q50raw = flatten(prediction?.quantiles?.['0.5']);
  const q50 = q50raw.length > 0 ? q50raw : point;
  const q90 = flatten(prediction?.quantiles?.['0.9']);
  if (!q50 || q50.length === 0) {
    return { value: null, bandPct: null, severity: 'neutral' };
  }
  const value = q50[q50.length - 1];
  const lo = q10.length === q50.length ? q10[q10.length - 1] : null;
  const hi = q90.length === q50.length ? q90[q90.length - 1] : null;
  let bandPct = null;
  if (
    typeof value === 'number' && value !== 0
    && typeof lo === 'number' && typeof hi === 'number'
  ) {
    bandPct = (Math.abs(hi - lo) / Math.abs(value)) * 100;
  }
  const severity =
    bandPct === null ? 'neutral'
    : bandPct < 1 ? 'success'
    : bandPct < 5 ? 'primary'
    : bandPct < 15 ? 'warning'
    : 'error';
  return { value, bandPct, severity };
};

const InferenceMiniChart = ({
  prediction,
  history,                  // [{x: ISO|Date, y: number}, ...] - optional
  intervalSec = 60,
  historyMaxPoints = 30,    // tail of history shown alongside forecast
  height = 64,
  maxPoints = 30,           // cap on forecast steps shown
  unit = null,              // measurement unit for tooltip values ("$/MWh")
}) => {
  const theme = useTheme();
  const fmtU = (v) => {
    const s = fmtNum(v);
    return unit && s !== '—' ? `${s} ${unit}` : s;
  };
  // Shared forecast accent (green) — one source of truth with the big charts.
  const accent = accentFor(theme.palette.mode);

  const point = flatten(prediction?.point);
  const q10 = flatten(prediction?.quantiles?.['0.1']);
  // See note in `predictionFinal` above: `flatten()` returns `[]` for
  // missing quantiles, and `[]` is truthy, so a `||` fallback never
  // fires. Custom trained models have no quantiles per the API
  // contract — fall back to `point` explicitly so the sparkline
  // still renders the median forecast.
  const q50raw = flatten(prediction?.quantiles?.['0.5']);
  const q50 = q50raw.length > 0 ? q50raw : point;
  const q90 = flatten(prediction?.quantiles?.['0.9']);

  if (!q50 || q50.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption" color="text.secondary">—</Typography>
      </Box>
    );
  }

  const len = Math.min(q50.length, maxPoints);
  const median = q50.slice(0, len);
  const lower = q10.slice(0, len);
  const upper = q90.slice(0, len);
  const haveBand = lower.length === len && upper.length === len;

  // Normalise history points to numeric x (we use indices, not dates,
  // because the sparkline has no axis). Layout: history occupies
  // indices [0 .. H-1]; the forecast anchor sits ON TOP of the last
  // history point at index H-1 (same x, same y) so the history line
  // and the forecast line meet exactly with no visible gap. Forecast
  // steps continue at H, H+1, ... When there's no history at all,
  // the forecast lays out on [0, 1, ...] like a plain sparkline.
  const histRaw = (history || [])
    .filter((p) => p.y !== null && p.y !== undefined)
    .slice(-historyMaxPoints);
  const haveHistory = histRaw.length > 0;
  const lastHistIdx = histRaw.length - 1;
  const lastHistVal = haveHistory ? histRaw[lastHistIdx].y : null;
  const forecastBaseIdx = haveHistory ? lastHistIdx : -1;

  const histPts = histRaw.map((p, i) => ({ x: i, y: p.y }));
  const anchorPt = haveHistory
    ? [{ x: lastHistIdx, y: lastHistVal }]
    : [];
  const forecastPts = [
    ...anchorPt,
    ...median.map((y, i) => ({ x: forecastBaseIdx + 1 + i, y })),
  ];
  const q10Pts = haveBand
    ? [
        ...anchorPt,
        ...lower.map((y, i) => ({ x: forecastBaseIdx + 1 + i, y })),
      ]
    : [];
  const q90Pts = haveBand
    ? [
        ...anchorPt,
        ...upper.map((y, i) => ({ x: forecastBaseIdx + 1 + i, y })),
      ]
    : [];

  // (Final-value chip moved out of the chart into its own column on
  // the inferences row; see ``predictionFinal`` above for the
  // exported helper the row uses to derive the same value + colour.)

  // Build chart data. Pass ALL series so nivo's auto y-scale sees
  // every value (history line + forecast median + quantile bounds).
  // The hidden ``_q10``/``_q90`` series only contribute to y-scale -
  // they're skipped in the custom line renderer below in favour of
  // the filled band layer.
  const chartData = [
    ...(haveHistory ? [{ id: 'history', data: histPts }] : []),
    { id: 'forecast', data: forecastPts },
    ...(haveBand ? [{ id: '_q10', data: q10Pts }] : []),
    ...(haveBand ? [{ id: '_q90', data: q90Pts }] : []),
  ];

  // Explicit y-scale across history + all three quantiles so nothing
  // gets clipped (the band layer is rendered as raw SVG, so nivo's
  // auto-scale would otherwise miss it).
  const allY = [
    ...histPts.map((p) => p.y),
    ...median,
    ...(haveBand ? lower : []),
    ...(haveBand ? upper : []),
  ].filter((v) => typeof v === 'number');
  let yMin = Math.min(...allY);
  let yMax = Math.max(...allY);
  if (!isFinite(yMin) || !isFinite(yMax)) { yMin = 0; yMax = 1; }
  const pad = (yMax - yMin) * 0.05 || Math.max(Math.abs(yMax), 0.01) * 0.05;
  const yScaleProps = {
    type: 'linear',
    min: yMin - pad,
    max: yMax + pad,
    stacked: false,
  };

  // Quantile band: filled area between p10 and p90, anchored at the
  // cutover (zero width there) so it tapers outward as uncertainty
  // grows. Uses q10Pts/q90Pts.length (not ``len``) because the
  // anchor adds one extra point when history is present.
  const QuantileBandLayer = ({ xScale, yScale }) => {
    if (!haveBand) return null;
    const n = q90Pts.length;
    if (n === 0) return null;
    let path = '';
    for (let i = 0; i < n; i++) {
      path += `${i === 0 ? 'M' : 'L'} ${xScale(q90Pts[i].x)},${yScale(q90Pts[i].y)} `;
    }
    for (let i = n - 1; i >= 0; i--) {
      path += `L ${xScale(q10Pts[i].x)},${yScale(q10Pts[i].y)} `;
    }
    path += 'Z';
    return (
      <path
        d={path}
        fill={alpha(accent, 0.18)}
        stroke="none"
      />
    );
  };

  // Custom line renderer:
  //   * history (when present) - neutral text-primary at 70% alpha,
  //     thinner stroke
  //   * forecast (p50) - primary palette, slightly thicker
  //   * _q10 / _q90 - hidden (their values feed y-scale, the band
  //     layer above is what users see)
  const SeriesLinesLayer = ({ series, xScale, yScale, lineGenerator }) => {
    const buildPath = (pts) =>
      lineGenerator(pts.map((p) => ({ x: xScale(p.data.x), y: yScale(p.data.y) })));
    return (
      <g>
        {series.map((s) => {
          if (s.id === '_q10' || s.id === '_q90') return null;
          if (s.id === 'history') {
            return (
              <path
                key={s.id}
                d={buildPath(s.data)}
                fill="none"
                stroke={alpha(theme.palette.text.primary, 0.7)}
                strokeWidth={1.4}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            );
          }
          if (s.id === 'forecast') {
            return (
              <path
                key={s.id}
                d={buildPath(s.data)}
                fill="none"
                stroke={accent}
                strokeWidth={1.75}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            );
          }
          return null;
        })}
      </g>
    );
  };

  // Cutover marker removed in the row sparkline: the visual hand-off
  // from the neutral history stroke to the primary forecast stroke
  // (and the leading edge of the quantile band) is enough at this
  // size. The big chart on the detail page still draws a labeled
  // "Now" line because there's room for it there.

  return (
    <Box sx={{ height, position: 'relative', px: 0.5 }}>
      <ResponsiveLine
        data={chartData}
        margin={{ top: 6, right: 6, bottom: 4, left: 6 }}
        xScale={{ type: 'linear' }}
        yScale={yScaleProps}
        curve="monotoneX"
        axisTop={null}
        axisRight={null}
        axisBottom={null}
        axisLeft={null}
        enableGridX={false}
        enableGridY={false}
        enablePoints={false}
        enableArea={false}
        layers={[
          'grid',
          'axes',
          QuantileBandLayer,
          SeriesLinesLayer,
          'crosshair',
          'mesh',
        ]}
        colors={[accent]}
        useMesh
        theme={{
          crosshair: {
            line: {
              stroke: accent,
              strokeWidth: 1,
              strokeOpacity: 0.35,
            },
          },
        }}
        tooltip={({ point: pt }) => {
          // Layout reminder: history spans [0..lastHistIdx], the
          // forecast anchor sits ON TOP of the last history point
          // (shared x), and forecast steps continue at
          // [lastHistIdx+1, ..., lastHistIdx+len]. So x <= lastHistIdx
          // is "show as history value", and x > lastHistIdx is
          // "forecast step (xi - lastHistIdx - 1)".
          const xi = pt.data.x;
          if (haveHistory && xi <= lastHistIdx) {
            return (
              <div style={tooltipStyle(theme)}>
                <div style={{ fontSize: '10px', color: theme.palette.text.secondary, marginBottom: 3 }}>
                  {xi === lastHistIdx ? 'Last actual (now)' : 'Actual'}
                </div>
                <div style={{ fontSize: '11px', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span>value</span><strong>{fmtU(pt.data.y)}</strong>
                </div>
              </div>
            );
          }
          const stepIdx = haveHistory ? xi - lastHistIdx - 1 : xi;
          const m = median[stepIdx];
          if (m === undefined) return null;
          const a = lower[stepIdx];
          const b = upper[stepIdx];
          return (
            <div style={tooltipStyle(theme)}>
              <div style={{ fontSize: '10px', color: theme.palette.text.secondary, marginBottom: 3 }}>
                Step {stepIdx + 1}
              </div>
              <div style={{ fontSize: '11px', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span>p50</span><strong>{fmtU(m)}</strong>
              </div>
              {a !== undefined && (
                <div style={{ fontSize: '11px', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span>p10</span><span>{fmtU(a)}</span>
                </div>
              )}
              {b !== undefined && (
                <div style={{ fontSize: '11px', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span>p90</span><span>{fmtU(b)}</span>
                </div>
              )}
            </div>
          );
        }}
      />
    </Box>
  );
};

export default InferenceMiniChart;
